import { useState } from 'react';
import { Pressable } from 'react-native';
import { Text, XStack, YStack } from 'tamagui';
import type { PlaceCatalogEntry } from '@my-fullstack-app/trpc-types';
import { SearchField, Skeleton, useThemeColors, Icon } from '@kiips/ui';
import { trpc } from '../../lib/trpc';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { usePlaceSearchSession } from '../../hooks/usePlaceSearchSession';

interface PlaceLookupRowProps {
  /** Called with the selected vendor-neutral catalog entry. The caller
   *  decides how to map fields onto its entity (e.g. Stay name /
   *  address / lat / lng / heroUrl). ADR-0044. */
  onSelect: (entry: PlaceCatalogEntry) => void;
  /** Optional placeholder text in the input. */
  placeholder?: string | undefined;
  /** When false, render disabled (no taps, no autocomplete). */
  canEdit: boolean;
  /** Header text shown above the input. */
  label?: string | undefined;
  /** When provided, the dropdown offers a final row that mints whatever
   *  the user typed as a plain name instead of a catalog pick. Callers
   *  that can store a name-only reference pass this; callers that
   *  genuinely require catalog coordinates omit it and the row never
   *  renders. */
  onUseRawText?: ((text: string) => void) | undefined;
}

/**
 * Compact place lookup row — type ≥2 chars, see autocomplete predictions
 * inline, tap one to call `onSelect(entry)`. Uses the neutral
 * places.search + places.lookup tRPC routes (ADR-0044), which fan out
 * across whatever providers are registered (Google today; Mapbox /
 * curated when added).
 *
 * With `onUseRawText`, the list ends in an escape hatch: "Use <what you
 * typed>". This mirrors the plain-entry row `AddVisitBody` already
 * offers Visit, but takes this component's own idiom — the dropdown is
 * one card of flat rows divided by hairlines, so the escape hatch is a
 * flat row marked by a dashed *rule*. A dashed box, as used in the tiled
 * Visit list, would nest a box inside a box here.
 */
export function PlaceLookupRow({
  onSelect,
  placeholder = 'Search places…',
  canEdit,
  label = 'Find a place',
  onUseRawText,
}: PlaceLookupRowProps) {
  const theme = useThemeColors();
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const debounced = useDebouncedValue(query, 250);
  // Session token bundles keystrokes + final pick into one Google
  // billing session. Reset on pick so the next search opens fresh.
  const { token: sessionToken, reset: resetSession } = usePlaceSearchSession();
  const { data: predictions = [], isLoading } = trpc.places.search.useQuery(
    { query: debounced, sessionToken },
    {
      enabled: debounced.length >= 2,
      staleTime: Infinity,
      gcTime: 1000 * 60 * 60,
    },
  );
  const utils = trpc.useUtils();

  const showResults = canEdit && query.length >= 2;

  // Reads the live query, not the debounced one: the row echoes the
  // user's own text back at them, so a 250ms lag between what they typed
  // and what the row offers to create would look broken.
  const rawText = query.trim();

  // Deliberately NOT gated on `isLoading`. React Query retries a failed
  // search with backoff, holding `isLoading` true for several seconds,
  // and that is exactly when a user gives up on the catalog and wants
  // their own words — hiding the escape hatch for the whole retry window
  // is the opposite of what's wanted. Shown alongside results too, not
  // only when there are none: a search for "Sakura Guest House" returns
  // plenty of guest houses named Sakura, and the user who meant their
  // own still needs a way out.
  const showMint = !!onUseRawText && showResults && rawText.length > 0 && !busy;

  const hasPredictions = !isLoading && predictions.length > 0;

  const handlePick = async (id: string) => {
    setBusy(true);
    setError(null);
    try {
      const entry = await utils.places.lookup.fetch({ id, sessionToken });
      if (entry) {
        resetSession();
        onSelect(entry);
        setQuery(''); // collapse the dropdown after a successful pick
      } else {
        setError('Could not load place details.');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to fetch place details.');
    } finally {
      setBusy(false);
    }
  };

  const handleUseRawText = () => {
    if (!onUseRawText || !rawText) return;
    setError(null);
    // Ends the Google Places billing session, exactly as a pick does.
    // Minting abandons the search, so the token must not carry over into
    // the next unrelated one.
    resetSession();
    onUseRawText(rawText);
    setQuery(''); // collapse the dropdown, same as a successful pick
  };

  return (
    <YStack gap={8}>
      <Text fontSize={11} color="$textSubdued" fontWeight="600">{label.toUpperCase()}</Text>
      <SearchField
        value={query}
        onChangeText={setQuery}
        editable={canEdit}
        placeholder={placeholder}
        accessibilityLabel="Search places"
      />

      {showResults ? (
        <YStack
          backgroundColor="$surface"
          borderRadius={14}
          overflow="hidden"
        >
          {/* Loading / empty / results stay one exhaustive ternary — they
              are mutually exclusive, and splitting them into separate
              guards made a reader re-derive that by hand. */}
          {isLoading ? (
            <YStack accessibilityRole="progressbar" accessibilityLabel="Searching places">
              {['s1', 's2', 's3'].map((slot, i) => (
                <YStack
                  key={slot}
                  paddingHorizontal={14}
                  paddingVertical={10}
                  gap={6}
                  borderTopWidth={i === 0 ? 0 : 1}
                  borderTopColor="$dividerSubtle"
                >
                  <Skeleton width="65%" height={14} />
                  <Skeleton width="40%" height={11} />
                </YStack>
              ))}
            </YStack>
          ) : predictions.length === 0 ? (
            <Text fontSize={12} color="$textSubdued" padding={12}>No matches</Text>
          ) : (
            predictions.slice(0, 5).map((p, i) => (
              <Pressable
                key={p.id}
                onPress={() => handlePick(p.id)}
                disabled={busy}
                accessibilityLabel={`Pick ${p.title}`}
                style={({ pressed }) => [
                  {
                    paddingHorizontal: 14,
                    paddingVertical: 10,
                    borderTopWidth: i === 0 ? 0 : 1,
                    borderTopColor: theme.dividerSubtle,
                  },
                  pressed && { backgroundColor: theme.surfaceCanvas },
                ]}
              >
                <Text fontSize={14} fontWeight="600" color="$textOnSurface" numberOfLines={1}>
                  {p.title}
                </Text>
                {p.subtitle ? (
                  <Text fontSize={11} color="$textSubdued" marginTop={1} numberOfLines={1}>
                    {p.subtitle}
                  </Text>
                ) : null}
              </Pressable>
            ))
          )}

          {/* Escape hatch. Flat row, dashed rule — see the component
              docblock. Sits above the Google attribution, never in
              place of it. */}
          {showMint ? (
            <Pressable
              onPress={handleUseRawText}
              accessibilityRole="button"
              accessibilityLabel={`Use “${rawText}” as the name, without a map listing`}
              style={({ pressed }) => [pressed ? { opacity: 0.7 } : null]}
            >
              <XStack
                paddingHorizontal={14}
                paddingVertical={12}
                alignItems="center"
                gap={12}
                borderTopWidth={1}
                borderTopColor="$borderLight"
                borderStyle="dashed"
              >
                <Text
                  fontSize={14}
                  fontWeight="600"
                  color="$textOnSurface"
                  flex={1}
                  textAlign="left"
                  numberOfLines={2}
                >
                  Use &ldquo;{rawText}&rdquo;
                </Text>
                <YStack
                  width={26}
                  height={26}
                  borderRadius={13}
                  backgroundColor="$surfaceCanvas"
                  alignItems="center"
                  justifyContent="center"
                  flexShrink={0}
                >
                  <Icon name="add" size={16} color={theme.textOnSurface} />
                </YStack>
              </XStack>
            </Pressable>
          ) : null}

          {/* Google Maps Platform branding policy — surfaces that display
              Places data must carry the attribution. Rendered only when
              predictions are actually on screen: with an empty result
              list there is no Places content to attribute. */}
          {hasPredictions ? (
            <XStack
              paddingHorizontal={14}
              paddingVertical={6}
              borderTopWidth={1}
              borderTopColor={theme.dividerSubtle}
              justifyContent="flex-end"
            >
              <Text fontSize={10} color="$textSubdued">Powered by Google</Text>
            </XStack>
          ) : null}
        </YStack>
      ) : null}

      {error ? (
        <XStack gap={6} alignItems="center" paddingHorizontal={4}>
          <Icon name="error-outline" size={14} color={theme.destructive} />
          <Text fontSize={12} color="$destructive">{error}</Text>
        </XStack>
      ) : null}
    </YStack>
  );
}
